import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { UPDATE_MULTICAST_MAPPING } from '@optools/services/operator-gateway/btc/mutations';
import { SEARCH_MULTICAST_MAPPING } from '@optools/services/operator-gateway/btc/queries';
import {
  PipelineMulticastMapping,
  SearchMulticastMappingQuery,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { MulticastMappingForm, MulticastMappingFormValues } from './multicast-mapping-form';

export interface UpdateMulticastMappingProps {
  multicastMapping: PipelineMulticastMapping;
}

export function UpdateMulticastMapping({ multicastMapping }: UpdateMulticastMappingProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { id, ...defaultValues } = multicastMapping;

  const [updateMulticastMapping, { loading }] = useMutation(UPDATE_MULTICAST_MAPPING, {
    onCompleted: ({ updateMulticastMapping }) => {
      if (!updateMulticastMapping) {
        toast.error('Update Multicast Mapping', {
          description: `Failed to update multicast mapping with id: ${id}`,
        });
        return;
      }

      toast.success('Update Multicast Mapping', {
        description: `Updated multicast mapping with id: ${updateMulticastMapping.id}`,
      });
      setIsOpen(false);
    },
    update: (cache, { data }) => {
      if (!data?.updateMulticastMapping) return;

      const multicastMappings = cache.readQuery<SearchMulticastMappingQuery>({
        query: SEARCH_MULTICAST_MAPPING,
      });

      if (!multicastMappings) return;

      cache.writeQuery<SearchMulticastMappingQuery>({
        data: {
          searchMulticastMappings: multicastMappings.searchMulticastMappings.map((item) =>
            item.id === data.updateMulticastMapping?.id ? data.updateMulticastMapping : item,
          ),
        },
        query: SEARCH_MULTICAST_MAPPING,
      });
    },
  });

  const onSubmit = (values: MulticastMappingFormValues) =>
    updateMulticastMapping({ variables: { id, updatedMapping: values } });

  const onCancel = () => setIsOpen(false);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button aria-label="update multicast mapping" size="icon" variant="outline">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Edit Multicast Mapping</DialogTitle>
        <DialogDescription className="sr-only">
          Edit an existing multicast mapping.
        </DialogDescription>
        <MulticastMappingForm
          defaultValues={defaultValues}
          disabled={loading}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      </DialogContent>
    </Dialog>
  );
}
